import { useEffect, useState } from 'react';
import { useAppDefinitions } from '../../apps/definition';
import { Bot } from '../../models/Bots';
import { UserApp } from '../../models/User';
import { invokeFastApi } from '../apis/fastapi';
import { logError } from '../utils';
import { useToaster } from './toast';

export interface IUpdateBotParams {
  id: string;
  bot_name?: string;
  is_autorespond?: boolean;
  custom_instructions?: string;
  bot_app_connections?: number[];
  enabled_apps?: UserApp[];
}

interface PatchBodyData {
  is_autorespond?: boolean;
  custom_instructions?: string;
  bot_app_connections?: number[];
}

interface BotsResponse {
  bots: Bot[];
  loading: boolean;
  error: boolean;
  orgWideEnabledApps: UserApp[];
  getBots: () => void;
  updateBot: (params: IUpdateBotParams) => Promise<void>;
}

export const useBots = (): BotsResponse => {
  const [bots, setBots] = useState<Bot[]>([]);
  const [orgWideEnabledApps, setOrgWideEnabledApps] = useState<UserApp[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const toaster = useToaster();
  const appDefinitions = useAppDefinitions();

  const getBots = async () => {
    setLoading(true);
    setError(false);
    setBots([]);
    try {
      const res = await invokeFastApi({
        path: '/bots',
        method: 'GET',
        queryParams: {
          v2: 'True',
        },
      });

      const { bots: fetchedBots, enabled_apps } = (await res) as {
        bots: Bot[];
        enabled_apps: UserApp[];
      };

      setBots(fetchedBots);
      const filteredEnabledApps = enabled_apps.filter(
        (app) => appDefinitions[app.appName]?.showInAppStore === true
      );

      setOrgWideEnabledApps(filteredEnabledApps);
    } catch (fetchError) {
      logError(fetchError);
      setError(true);
      toaster.failure('Failed to fetch bots');
    }

    setLoading(false);
  };

  const updateBot = async ({
    id,
    custom_instructions,
    is_autorespond,
    bot_app_connections,
    enabled_apps,
  }: IUpdateBotParams) => {
    const bodyData: PatchBodyData = {};

    if (custom_instructions) {
      bodyData.custom_instructions = custom_instructions;
    }

    if (is_autorespond) {
      bodyData.is_autorespond = is_autorespond;
    }

    if (bot_app_connections) {
      bodyData.bot_app_connections = bot_app_connections;
    }

    try {
      await invokeFastApi({
        path: `/bots/${id}`,
        method: 'PATCH',
        body: bodyData,
      });

      setBots((prevBots) => {
        const botIndex = prevBots.findIndex((bot) => bot.id === id);
        if (botIndex === -1) {
          // Bot not found, return the previous state
          return prevBots;
        }

        const prevBot = prevBots[botIndex]!;

        // Create a new bot object with the updated values
        const updatedBot = {
          ...prevBots[botIndex],
          is_autorespond: is_autorespond ?? prevBot.is_autorespond,
          custom_instructions:
            custom_instructions ?? prevBot.custom_instructions,
          apps:
            bot_app_connections && enabled_apps
              ? enabled_apps.filter((app) =>
                  bot_app_connections.includes(app.id)
                )
              : prevBot.apps,
        };

        // Create a new array with the updated bot
        const newBots = [
          ...prevBots.slice(0, botIndex),
          updatedBot,
          ...prevBots.slice(botIndex + 1),
        ];

        return newBots as Bot[];
      });

      toaster.success('Bot updated successfully');
    } catch (fetchError) {
      logError(fetchError);
      toaster.failure('Failed to update bot');
    }
  };

  useEffect(() => {
    getBots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { bots, orgWideEnabledApps, loading, error, getBots, updateBot };
};
